// Pure derivations for the home VM (ARCHITECTURE §4.3 rule 5). The
// controller (useHomePage) and the fixtures both build the VM through these,
// so the values a template sees in tests are the values it sees live.
//
// Every rule here is lifted verbatim from today's pages/Home.tsx (section
// filtering and ordering, live-first sort, sectionTitle(), the about/links
// self-hide inputs, #260 link sanitising) and components/SwipeableStandings
// (bracket detection, column choice).

import type { SportPack } from '@/contexts/SportContext'
import type {
  Announcement, DivisionRef, FeaturedItem, GalleryImage, HomepageData, HomepageSection, HomepageSponsorGroup,
  QuickLink, RecentResult, Team, TeamStanding, UpcomingGame,
} from '@/services/api/types'
import type { TenantInfo } from '@/contexts/LeagueContext'
import { getImageUrl } from '@/services/api'
import { formatEventDateParts } from '@/utils/timezone'
import { deriveTeamCode } from '@/utils/teamDisplay'
import { isAbsoluteHref, safeHref } from '@/utils/safeHref'
import { ADDON_KEYS, SITE_SECTION_KEYS, addonEntitled, sectionEnabled } from '@/contexts/tenantAccess'
import type { Gating } from '../../template/pages'
import { fmtDate, fmtNumber, getLocale, messages } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import type {
  AnnouncementVM, EventWhen, FeaturedStandingVM, GalleryItemVM, HomePageVM, HomeSectionType, HomeSlot,
  OpenFormVM, QuickLinkVM, RecentResultVM, SponsorTierVM, StandingRowVM, TeamCardVM, TeamRecordVM, TeamRef, UpcomingGameVM,
  VenueRef,
} from './types'
import type { MemberRegistrationCard } from '@/services/memberApi'
import { usd } from '@/site/kit/forms/fields'
import { dayText, registrationHref } from '../registrations/derive'

/** Default section titles, in the active language (a league can rename each one). */
export function homeDefaultTitles(): Record<HomeSectionType, string> {
  const s = messages(siteCoreMessages).pages.home.sections
  return {
    standings: s.standings,
    upcoming: s.upcoming,
    recent_results: s.recentResults,
    sponsors: s.sponsors,
    announcements: s.announcements,
    gallery: s.gallery,
    highlight: s.highlight,
    about: s.about,
    links: s.links,
  }
}

// ── Small shared builders (schedule and game use them too) ─────────────────

export function teamCode(name: string, shortCode: string | null | undefined): string {
  const code = shortCode?.trim()
  return code ? code.toUpperCase() : deriveTeamCode(name)
}

export function teamRef(t: { id: number; name: string; shortCode?: string | null; short_code?: string | null; logo?: string | null; logo_url?: string | null; primary_color?: string | null }): TeamRef {
  const shortCode = (t.shortCode ?? t.short_code ?? null) || null
  return {
    id: t.id,
    name: t.name,
    shortCode,
    code: teamCode(t.name, shortCode),
    logoUrl: getImageUrl(t.logo ?? t.logo_url ?? null),
    color: t.primary_color ?? null,
    href: `/teams/${t.id}`,
  }
}

export function eventWhen(startsAt: string | null, timezone: string | null): EventWhen {
  const d = startsAt ? new Date(startsAt) : null
  const ok = d !== null && !Number.isNaN(d.getTime())
  const timeZone = timezone || 'America/Chicago'
  const fmt = (o: Intl.DateTimeFormatOptions) => (ok ? fmtDate(d!, { ...o, timeZone }) : null)
  return {
    startsAt,
    timezone,
    ...formatEventDateParts(startsAt, timezone),
    weekday: fmt({ weekday: 'short' }),
    weekdayLong: fmt({ weekday: 'long' }),
    dateLong: fmt({ month: 'long', day: 'numeric' }),
  }
}

/** #455: `venue { id, name, address, map_url }` on game lists (courts inherit the building's address). */
interface PayloadVenue { id: number; name: string; address: string | null; map_url: string | null }

export function venueRef(g: { venue_id: number | null; venue_name: string | null; parent_venue_id: number | null; parent_venue_name: string | null; venue?: PayloadVenue | null }): VenueRef | null {
  if (!g.venue_name) return null
  return {
    id: g.venue_id,
    name: g.venue_name,
    parentId: g.parent_venue_id,
    parentName: g.parent_venue_name,
    address: g.venue?.address || null,
    mapHref: safeHref(g.venue?.map_url) ?? null,
  }
}

/** Baseball-style share: '.750', '1.000'. */
export function formatPct(share: number): string {
  const s = fmtNumber(Math.min(1, Math.max(0, Math.round(share * 1000) / 1000)), { minimumFractionDigits: 3, maximumFractionDigits: 3 })
  // English standings drop the leading zero (.750); French and Spanish keep it (0,750) (#1129).
  return getLocale() === 'en' ? s.replace(/^0(?=\.)/, '') : s
}

/** Games behind a leader (draws count half): '—' for the leader, else '1.5' / '2'. */
export function gamesBehind(leader: { wins: number; losses: number }, row: { wins: number; losses: number }): string {
  const gb = ((leader.wins - row.wins) + (row.losses - leader.losses)) / 2
  return gb <= 0 ? '—' : String(gb)
}

export function gatingOf(league: TenantInfo | null): Gating {
  return {
    sections: Object.fromEntries(SITE_SECTION_KEYS.map((k) => [k, sectionEnabled(league, k)])) as Gating['sections'],
    addons: Object.fromEntries(ADDON_KEYS.map((k) => [k, addonEntitled(league, k)])) as Gating['addons'],
  }
}

// ── Layout ─────────────────────────────────────────────────────────────────

export function sectionTitle(league: TenantInfo | null, type: HomeSectionType, titles: Record<HomeSectionType, string>): string {
  if (type === 'about') return league?.section_overrides?.about?.title ?? titles.about
  return league?.section_overrides?.[type]?.title || titles[type]
}

function slotOf(s: HomepageSection, league: TenantInfo | null, titles: Record<HomeSectionType, string>): HomeSlot {
  return { key: String(s.id), type: s.section_type, title: sectionTitle(league, s.section_type, titles), column: s.column_position }
}

export function deriveLayout(layout: HomepageSection[], league: TenantInfo | null, titles: Record<HomeSectionType, string> = homeDefaultTitles()): HomePageVM['layout'] {
  if (layout.length === 0) {
    const standings: HomeSlot = { key: 'fallback-standings', type: 'standings', title: sectionTitle(league, 'standings', titles), column: 'left' }
    const upcoming: HomeSlot = { key: 'fallback-upcoming', type: 'upcoming', title: sectionTitle(league, 'upcoming', titles), column: 'right' }
    return { desktop: { left: [standings], right: [upcoming], full: [] }, mobile: [standings, upcoming], usedFallback: true }
  }
  // Sponsors are an add-on (1.0 launch, P2): without it the section isn't
  // drawn at all, rather than as an empty box (the server sends no sponsors).
  const shown = (s: HomepageSection) => s.is_active && (s.section_type !== 'sponsors' || addonEntitled(league, 'sponsors'))
  const col = (c: HomepageSection['column_position']) => layout
    .filter((s) => s.column_position === c && shown(s))
    .sort((a, b) => a.display_order - b.display_order)
    .map((s) => slotOf(s, league, titles))
  const mobile = layout
    .filter(shown)
    .sort((a, b) => a.mobile_display_order - b.mobile_display_order)
    .map((s) => slotOf(s, league, titles))
  return { desktop: { left: col('left'), right: col('right'), full: col('full') }, mobile, usedFallback: false }
}

// ── Sections ───────────────────────────────────────────────────────────────

/** Today's SwipeableStandings rule: a competition whose rows carry a bracket status. */
export function isBracketItem(f: FeaturedItem): boolean {
  return f.standings.length > 0 && f.standings.some((t) => t.status !== undefined)
}

export function standingRow(t: TeamStanding, leader?: TeamStanding): StandingRowVM {
  return {
    raw: t,
    rank: t.rank,
    team: teamRef({ id: t.id, name: t.name, short_code: t.short_code, logo_url: t.logo_url, primary_color: t.primary_color }),
    pct: formatPct(typeof t.winPct === 'number' ? t.winPct : 0),
    gb: gamesBehind(leader ?? t, t),
    seed: t.seed ?? null,
    bracketStatus: t.status ?? null,
  }
}

/** Rows for one table; GB is measured from the first row (the API's rank order). */
export function standingRows(rows: TeamStanding[]): StandingRowVM[] {
  return rows.map((r) => standingRow(r, rows[0]))
}

/**
 * @param packForSeason the pack today's StandingsTable resolves via
 *   useSportForSeason(seasonId): the season's sport, else the league default.
 */
export function deriveFeatured(featured: FeaturedItem[], packForSeason: (seasonId: number | null) => SportPack): FeaturedStandingVM[] {
  return featured.map((f) => {
    const bracket = isBracketItem(f)
    const isCompetition = f.type === 'tournament'
    const kind: FeaturedStandingVM['kind'] = isCompetition && bracket ? 'bracket' : 'table'
    const columns = bracket ? [] : f.rankingStrategy?.columns ?? packForSeason(f.type === 'season' ? f.contentId : null).standings.columns
    return {
      ...f,
      key: `${f.type}-${f.contentId}`,
      isCompetition,
      kind,
      columns,
      competitionId: isCompetition ? f.contentId : null,
      scheduleHref: isCompetition ? `/schedule/${f.contentId}` : null,
      rows: standingRows(f.standings),
    }
  })
}

/** Live games first (stable otherwise), at most 6, as Home's upcoming card does. */
export function deriveUpcoming(games: UpcomingGame[], tag?: (divisionId: number | null | undefined) => DivisionRef | null): UpcomingGameVM[] {
  return [...games]
    .sort((a, b) => {
      if (a.status === 'in_progress' && b.status !== 'in_progress') return -1
      if (a.status !== 'in_progress' && b.status === 'in_progress') return 1
      return 0
    })
    .slice(0, 6)
    .map((g) => ({
      id: g.id,
      href: `/game/${g.id}`,
      status: g.status,
      live: g.status === 'in_progress',
      when: eventWhen(g.date, g.timezone),
      home: { ...teamRef(g.homeTeam), score: g.homeTeam.score ?? null },
      away: { ...teamRef(g.awayTeam), score: g.awayTeam.score ?? null },
      venue: venueRef(g),
      hasStream: g.hasStream,
      competitionName: g.tournamentName,
      // #583: only with 2+ live divisions.
      ...(tag ? { division: tag(g.division_id) } : {}),
    }))
}

export function deriveResults(results: RecentResult[], tag?: (divisionId: number | null | undefined) => DivisionRef | null): RecentResultVM[] {
  return results.map((r) => ({
    id: r.id,
    href: `/game/${r.id}`,
    when: eventWhen(r.date, r.timezone),
    home: { ...teamRef(r.homeTeam), score: r.homeTeam.score },
    away: { ...teamRef(r.awayTeam), score: r.awayTeam.score },
    winner: r.homeTeam.score > r.awayTeam.score ? 'home' : r.awayTeam.score > r.homeTeam.score ? 'away' : null,
    competitionName: r.tournamentName,
    ...(tag ? { division: tag(r.division_id) } : {}),
  }))
}

export function deriveSponsors(groups: HomepageSponsorGroup[]): SponsorTierVM[] {
  return groups.map((g, i) => ({
    id: g.levelId,
    name: g.levelName,
    rank: i,
    sponsors: g.sponsors.map((s) => ({ id: s.id, name: s.name, logoUrl: getImageUrl(s.logo_url), href: `/sponsor/${s.id}` })),
  }))
}

export function deriveAnnouncements(items: Announcement[]): AnnouncementVM[] {
  return items.map((a) => {
    // #260: only http(s)/mailto/tel or a same-site path becomes a link.
    const href = safeHref(a.link_url, { relative: true })
    return {
      key: String(a.id),
      label: a.label,
      text: a.text,
      link: href ? { href, external: isAbsoluteHref(href) } : null,
      ticketSaleHref: a.ticket_sale_slug ? `/tickets/${a.ticket_sale_slug}` : null,
      isLiveEvent: a.isLiveEvent,
    }
  })
}

export function deriveGallery(images: GalleryImage[]): GalleryItemVM[] {
  return images.map((img) => ({
    id: img.id,
    thumbUrl: getImageUrl(img.thumbnail_path),
    fullUrl: getImageUrl(img.file_path),
    title: img.title,
    alt: img.title || messages(siteCoreMessages).pages.home.galleryImageAlt,
  }))
}

export function deriveQuickLinks(links: QuickLink[]): QuickLinkVM[] {
  return links.map((l) => ({
    id: l.id,
    label: l.label,
    // #260: an unsafe stored external_url renders as a dead button, not a link.
    href: l.isExternal ? safeHref(l.href) ?? null : l.href,
    external: l.isExternal,
  }))
}

/** Records from the featured item for the active season (type 'season', contentId = season id). */
export function deriveRecords(featured: FeaturedItem[], seasonId: number | null | undefined): Record<number, TeamRecordVM> {
  const out: Record<number, TeamRecordVM> = {}
  if (seasonId == null) return out
  const item = featured.find((f) => f.type === 'season' && f.contentId === seasonId)
  for (const row of item?.standings ?? []) {
    const draws = typeof row.draws === 'number' ? row.draws : null
    out[row.team_id] = {
      wins: row.wins,
      losses: row.losses,
      draws,
      text: draws === null ? `${row.wins}-${row.losses}` : `${row.wins}-${draws}-${row.losses}`,
      rank: row.rank,
      pct: formatPct(typeof row.winPct === 'number' ? row.winPct : 0),
    }
  }
  return out
}

/** Fill game-card team colours from the homepage teams list (game payloads carry none). */
function withColors<T extends { home: TeamRef; away: TeamRef }>(items: T[], teams: Team[]): T[] {
  const color = new Map(teams.map((t) => [t.id, t.primary_color ?? null]))
  return items.map((g) => ({
    ...g,
    home: { ...g.home, color: g.home.color ?? color.get(g.home.id) ?? null },
    away: { ...g.away, color: g.away.color ?? color.get(g.away.id) ?? null },
  }))
}

export function deriveTeams(teams: Team[]): TeamCardVM[] {
  return teams.map((t) => ({ ...teamRef(t), city: t.city }))
}

export function aboutContent(league: TenantInfo | null): string | null {
  const raw = league?.section_overrides?.about?.content
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : null
}

// ── The whole VM ───────────────────────────────────────────────────────────

export interface HomeInputs {
  league: TenantInfo | null
  sport: SportPack
  packForSeason: (seasonId: number | null) => SportPack
  data: HomepageData | null
  galleryImages: GalleryImage[]
  loading: boolean
  error: string | null
  titles?: Record<HomeSectionType, string>
  /** The registration spotlight (deriveOpenRegistrations); [] when absent. */
  openForms?: OpenFormVM[]
  /** #583: tags upcoming and result rows with their division (2+ live divisions only). */
  divisionTag?: (divisionId: number | null | undefined) => DivisionRef | null
}

export type HomeVMData = Omit<HomePageVM, 'retry' | 'dismissError' | 'division'>

export function deriveHome(i: HomeInputs): HomeVMData {
  const { league, data, loading } = i
  const titles = i.titles ?? homeDefaultTitles()
  const gating = gatingOf(league)
  const announcements = deriveAnnouncements(data?.announcements || [])
  return {
    status: loading && !data ? 'loading' : i.error && !data ? 'error' : 'ready',
    error: i.error,
    refreshing: loading && !!data,
    sport: i.sport,
    league: {
      name: league?.name ?? messages(siteCoreMessages).pages.leagueFallback,
      logoUrl: league?.logo_url ?? null,
      heroImageUrl: league?.branding?.hero_image_url ?? '/banner.png',
      tagline: league?.copy?.hero_tagline ?? null,
      communityQuote: league?.copy?.community_quote || null,
    },
    season: data?.activeSeason ?? null,
    layout: deriveLayout(data?.layout || [], league, titles),
    data: {
      standings: { featured: deriveFeatured(data?.featured || [], i.packForSeason), loading },
      upcoming: { games: withColors(deriveUpcoming(data?.upcomingGames || [], i.divisionTag), data?.teams || []), total: (data?.upcomingGames || []).length, loading },
      recent_results: { results: withColors(deriveResults(data?.recentResults || [], i.divisionTag), data?.teams || []), loading },
      sponsors: { tiers: deriveSponsors(data?.sponsors || []) },
      announcements: { items: announcements, hasLive: announcements.some((a) => a.isLiveEvent) },
      gallery: { images: i.galleryImages, items: deriveGallery(i.galleryImages), viewAllHref: gating.sections.gallery ? '/gallery' : null },
      highlight: null,
      about: { content: aboutContent(league), title: sectionTitle(league, 'about', titles) },
      links: { items: deriveQuickLinks(data?.quickLinks || []) },
    },
    teams: { items: deriveTeams(data?.teams || []), loading, linkTeams: gating.sections.teams },
    gating,
    // The registration spotlight (October 28): useHomePage fills it from
    // the member Register list; 1.0's open forms went with Forms (step R).
    openForms: i.openForms ?? [],
    records: deriveRecords(data?.featured || [], data?.activeSeason?.id),
  }
}

/**
 * The registration spotlight (Website › Design › Registration, October 28)
 * from the member Register list's cards: only those someone can join from
 * the page now or soon (open, the waitlist, a team registration a captain
 * registers a team for, or not yet open), never a full, closed or unpayable
 * one (no Stripe, invite only), for the active sport when the league runs
 * more than one (`sportId`), in the list's order (open ones first, then
 * upcoming). An upcoming card (sign_up.state 'not_yet_open') carries
 * opensLabel.
 */
function joinable(c: MemberRegistrationCard): boolean {
  const st = c.sign_up?.state
  if (st === 'open' || st === 'waitlist' || st === 'not_yet_open') return true
  return st === 'not_available' && c.sign_up?.reason === 'team'
}

export function deriveOpenRegistrations(
  cards: MemberRegistrationCard[],
  tz: string | null,
  sportId: string | null,
): OpenFormVM[] {
  return cards
    .filter((c) => joinable(c) && (!sportId || !c.season?.sport_id || c.season.sport_id === sportId))
    .map((c) => {
      const upcoming = c.sign_up?.state === 'not_yet_open'
      return {
        slug: c.slug,
        title: c.name,
        href: registrationHref(c.slug),
        priceCents: c.fee_cents > 0 ? c.fee_cents : null,
        currency: 'usd',
        price: c.fee_cents > 0 ? usd(c.fee_cents) : null,
        waitlist: c.sign_up?.state === 'waitlist',
        closesAt: c.closes_at,
        closesLabel: dayText(c.closes_at, tz),
        opensLabel: upcoming ? dayText(c.opens_at, tz) ?? null : null,
      }
    })
}
